import React, {useState} from 'react';

import {Link, withRouter, WithRouterProps} from 'react-router';

import {useDeleteFolder} from 'app/folders/api';
import {EditFolder} from 'app/folders/components/dashboards/EditFolder';
import {useMoveModal} from 'app/folders/components/MoveFolderModal';
import {StyledCompactItemHeaderSpan} from 'app/general/bootstrap/utils';
import {getFilterLink} from 'app/general/dashboards/DashboardFilterbar';
import {
  type BadgeRelation,
  ItemRelationsBadges,
} from 'app/general/dashboards/ItemRelationsBadges';
import {ItemRelationsListOfGenerics} from 'app/general/dashboards/ItemRelationsListOfGenerics';
import {ItemRelationsLists} from 'app/general/dashboards/ItemRelationsLists';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {useDeleteModal} from 'app/kbs/components/KBEditPage/KBTree/KBTreeDeleteItemModal';
import {DocumentIconWithNotifications} from 'app/notifly/components/DocumentIconWithNotifications';
import {DashboardItemPresentMembers} from 'app/presence/components/DashboardItemPresentMembers';
import {getObjectName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';

import type {DashboardLocationParamsType} from 'app/documents/types';
import type {
  BasicTeamSerializedType,
  ContentType,
  FolderSerializedType,
} from 'sweet-types';

const relations: Array<BadgeRelation> = [
  {
    ct: 'team',
    getter: (p: FolderSerializedType) => p.teams,
    Component: ItemRelationsListOfGenerics,
  },
];

interface Props extends WithRouterProps<DashboardLocationParamsType> {
  object: FolderSerializedType;
  refreshData: () => Promise<unknown>;
  allowedTeams: Array<BasicTeamSerializedType>;
  activeRelations: Record<ContentType, boolean>;
  toggleRelation: (contentType: ContentType, enabled: boolean) => unknown;
}

export const CompactListFolderItem = withRouter(
  ({
    object,
    router,
    location,
    refreshData,
    allowedTeams,
    toggleRelation,
    activeRelations,
  }: Props) => {
    const setDeleting = useDeleteModal();
    const {show: showMoveModal, MoveModal} = useMoveModal();
    const [deleteFolder] = useDeleteFolder();
    const [editFolder, setEditFolder] = useState<boolean>(false);

    const to = getFilterLink(router, location, {
      folder: object.hashid,
    });

    return editFolder ? (
      <EditFolder
        item={object}
        parent={{teams: allowedTeams}}
        onClose={() => {
          refreshData().then(() => setEditFolder(false));
        }}
        inPopover={false}
      />
    ) : (
      <div
        className="row border-bottom py-2 d-flex align-items-center justify-content-between"
        style={{position: 'relative', background: '#f7f9fa'}}
      >
        <DashboardItemPresentMembers
          contentType={'folder'}
          objectId={object.id}
        />

        <div className="col-12 col-lg d-flex flex-column">
          <Link to={to} className="bold d-block d-sm-inline">
            <StyledCompactItemHeaderSpan>
              <DocumentIconWithNotifications
                contentType={'folder'}
                notifications={[]}
              />
              {getObjectName(object, 'folder', true)}
            </StyledCompactItemHeaderSpan>
          </Link>
        </div>

        <ItemRelationsBadges
          className="col-auto order-lg-3"
          relations={relations}
          object={object}
          activeRelations={activeRelations}
          onToggle={toggleRelation}
        />

        <div className="col-auto order-lg-5">
          {object.can_edit ? (
            <ActionsButton
              actions={[
                {
                  icon: 'fa fa-edit',
                  text: gt('Edit'),
                  callback: () => setEditFolder(true),
                  key: 'fa fa-edit',
                },
                {
                  icon: 'fa fa-arrows-alt',
                  text: gt('Move'),
                  callback: () =>
                    showMoveModal({
                      object,
                      action: () => Promise.resolve(true),
                      callback: refreshData,
                      help: gt(
                        'When you move this folder, all its contents will move with it. It will adopt the teams of the folder you place it into.'
                      ),
                    }),
                  key: 'fa fa-arrows-alt',
                },
                {
                  text: gt('Delete'),
                  icon: 'far fa-trash',
                  key: 'remove-folder',
                  callback: () =>
                    setDeleting({
                      object,
                      action: deleteFolder,
                      callback: refreshData,
                      help: gt(
                        'This folder and all sub folders and documents will be deleted.'
                      ),
                    }),
                },
              ]}
            />
          ) : null}
        </div>

        <div className="col-12 order-last">
          <ItemRelationsLists
            object={object}
            activeRelations={activeRelations}
            relations={relations}
          />
        </div>
        {MoveModal}
      </div>
    );
  }
);
